import * as React from "react";
import { useState, useRef, useEffect, useContext } from "react";
import MOTION from "@sproutsocial/seeds-motion/unitless";
import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";
import { DisablePortalToBodyContext } from "@sproutsocial/seeds-react-portal";
import Box from "@sproutsocial/seeds-react-box";
import TooltipHybrid from "./TooltipHybrid";
import type { TypeTooltipProps } from "./TooltipTypes";

let idCounter = 0;

const hasAttribute = (child: React.ReactNode, attribute: string) => {
  return React.isValidElement(child) && child.props[attribute] !== undefined;
};

type Side = "top" | "bottom" | "left" | "right";
type Align = "start" | "center" | "end";

// `auto`, `auto-start`, and `auto-end` resolve to `side: "top"` (with align
// taken from the suffix) and rely on base-ui's Positioner collision flipping at
// runtime, rather than Popper's old best-initial-side selection. Exported for
// unit testing of this mapping.
export function mapPlacement(placement: string): { side: Side; align: Align } {
  const parts = placement.split("-");
  let side: Side;
  let align: Align = "center";

  switch (parts[0]) {
    case "bottom":
      side = "bottom";
      break;
    case "left":
      side = "left";
      break;
    case "right":
      side = "right";
      break;
    case "top":
    case "auto":
    default:
      side = "top";
      break;
  }

  if (parts[1] === "start") {
    align = "start";
  } else if (parts[1] === "end") {
    align = "end";
  }

  return { side, align };
}

const Tooltip = ({
  content,
  children,
  enterDelay = MOTION.MOTION_DURATION_FAST * 1000,
  exitDelay,
  placement = "auto",
  appearance,
  zIndex = 7,
  qa,
  popoutProps,
  triggerProps,
  truncated = false,
  onFocus,
  onBlur,
  legacyMouseInteraction,
  ...rest
}: TypeTooltipProps) => {
  const [open, setOpen] = useState(false);
  const [id] = useState(`Racine-tooltip-${idCounter++}`);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const isInvalidContent = content === null || content === undefined;
  const disablePortalToBody = useContext(DisablePortalToBodyContext);
  const [portalContainer, setPortalContainer] = useState<
    HTMLElement | undefined
  >(undefined);

  const resolvedExitDelay =
    exitDelay !== undefined
      ? exitDelay
      : legacyMouseInteraction
      ? MOTION.MOTION_DURATION_FAST * 1000
      : 0;

  const defaultAppearance =
    appearance || (typeof content === "object" ? "box" : "pill");

  const { side, align } = mapPlacement(placement);

  const mergedWrapperProps = {
    ...popoutProps,
    ...triggerProps,
  };

  const hasWrapperProps = Object.keys(mergedWrapperProps).length > 0;

  useEffect(() => {
    if (disablePortalToBody && triggerRef.current) {
      const dialogContent =
        triggerRef.current.closest<HTMLElement>("[role='dialog']");
      setPortalContainer(dialogContent ?? undefined);
    }
  }, [disablePortalToBody]);

  if (isInvalidContent) {
    return (
      <span
        style={
          truncated
            ? {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }
            : {}
        }
      >
        {children}
      </span>
    );
  }

  const tooltipTree = (
    <BaseTooltip.Root
      open={open}
      onOpenChange={setOpen}
      disabled={isInvalidContent}
      disableHoverablePopup={!legacyMouseInteraction}
    >
      <BaseTooltip.Trigger
        delay={enterDelay}
        closeDelay={resolvedExitDelay}
        render={
          <span
            ref={triggerRef}
            onFocus={onFocus}
            onBlur={onBlur}
            data-qa-popout-isopen={open ? "true" : "false"}
            data-qa-tooltip={id}
            {...(qa || {})}
            style={
              truncated
                ? {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                  }
                : undefined
            }
          />
        }
      >
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement, {
              "aria-expanded": hasAttribute(children, "aria-expanded")
                ? children.props["aria-expanded"]
                : undefined,
              "aria-describedby": open ? id : undefined,
            })
          : children}
      </BaseTooltip.Trigger>
      <BaseTooltip.Portal container={portalContainer}>
        <BaseTooltip.Positioner
          side={side}
          align={align}
          sideOffset={4}
          style={{ zIndex }}
        >
          <BaseTooltip.Popup
            id={id}
            render={
              <TooltipHybrid
                appearance={defaultAppearance}
                tabIndex={legacyMouseInteraction ? 0 : undefined}
                {...rest}
              />
            }
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );

  return hasWrapperProps ? (
    <Box {...mergedWrapperProps}>{tooltipTree}</Box>
  ) : (
    tooltipTree
  );
};

export default Tooltip;
